<template>
  <div class="customization-music-wrapper">
    <div v-if="customConfig?.length" class="wrapper">
      <div 
        v-for="(config, i) in customConfig" 
        :key="config.name" 
        class="customily_music_option" 
        :sort-id="config.sortId"
        :ref="el => fieldRefs[config.sortId] = el"
      >
        <div v-if="config && config.type == 'options'" class="mb-sm">
          <div class="customization-music-custom-field-title">
            {{ config?.title || 'Choose Option *' }}
            <span v-if="config?.required" class="text-red-500">*</span>
            <span
              v-if="config.error"
              class="text-red-500 ml-1"
              style="color: red;"
            >
              Required
            </span>
          </div>

          <sib-select 
            size="md"
            :options="config?.listValue?.split(',').map(item => ({ value: item, label: item }))"
            :value="config.customValue ?? config?.listValue?.split(',')[0]"
            @input="val => { config.customValue = val }"
            :required="config?.required === true"
          />
        </div>

        <div v-else-if="config && config.type == 'text'" class="customization-music-custom-field-item mb-sm">
          <div class="customization-music-custom-field-title">
            {{ config?.title || 'Enter Name *' }}
            <span v-if="config?.required" class="text-red-500">*</span>
            <span
              v-if="config.error"
              class="text-red-500 ml-1"
              style="color: red;font-weight: 500;"
            >
              Required
            </span>
          </div>

          <sib-input 
            v-model="config.customValue" 
            :placeholder="config?.placeHolder || 'Eg: Emma, Lyly, ...'" 
            size="md"
            round="md"
            maxLength='255'
            :required="config?.required === true"
          ></sib-input>
        </div>
      </div>
    </div>

    <button
      type="button"
      class="music-action-button"
      @click="handleGenMusic"
      :disabled="status === 'waiting'"
    >
      <span v-if="status === 'waiting'">⏳ Generating...</span>
      <span v-else><span class="music-icon">♬</span> Preview Your Song </span>
    </button>

    <div v-if="showMusicPreviewError && !musicFile" ref="musicPreviewErrorRef" class="text-red-500 mt-xs mb-xs">
      <span style="color: red; font-weight: 500;">Required: Please click "Preview Your Song" before adding to cart.</span>
    </div>

    <!-- Progress Bar -->
    <div v-if="showProgress" class="music-progress-wrapper">
      <div class="music-progress-bar">
        <div
          class="music-progress-fill"
          :style="{ width: progress + '%' }"
        ></div>
      </div>
      <div class="music-progress-text">
        Generating music... {{ Math.floor(progress) }}%
      </div>
    </div>

    <!-- WaveSurfer Player -->
    <div
      v-if="status === 'done' && musicFile"
      class="wave-player spotify-style" style="margin-top: 15px"
    >
      <div class="wave-time">
        <span>{{ formatTime(currentTime) }}</span>
        <span>{{ formatTime(remainingTime) }}</span>
      </div>

      <div ref="waveformRef" class="waveform"></div>
    </div>

    <!-- Play Music -->
    <div v-if="status === 'done' && musicFile" class="music-player">
      <button
        type="button"
        class="music-action-button"
        @click="togglePlayMusic"
      >
        <svg
          v-if="!isPlaying"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="white"
          xmlns="http://www.w3.org/2000/svg"
        >
          <!-- Play icon -->
          <path d="M8 5v14l11-7z"/>
        </svg>

        <svg
          v-else
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="white"
          xmlns="http://www.w3.org/2000/svg"
        >
          <!-- Stop icon -->
          <path d="M6 6h12v12H6z"/>
        </svg>

        {{ isPlaying ? 'Stop Music' : 'Play Music' }}
      </button>
    </div>

    <div v-if="status === 'done' && lyrics" class="music-lyrics">
      <h4 style="margin-bottom: 4px;">🎶 Lyrics</h4>

      <pre class="lyrics-content">
        {{ displayedLyrics }}
      </pre>

      <button
        v-if="hasMoreLyrics"
        type="button"
        class="lyrics-toggle"
        @click="isLyricsExpanded = !isLyricsExpanded"
      >
        {{ isLyricsExpanded ? 'Collapse' : 'See more' }}
      </button>
    </div>

    <div class="rate-limit-transfer-message" v-show="exceedTheLimit && !isTechLeversphereBypass">
      <b>You’ve used all your free previews.</b>
      <br />Add this product to cart to unlock <b>10 more preview attempts.</b>
    </div>

    <!-- Hidden input for music AI URL -->
    <input type="hidden" name="properties[_music_ai_url]" :value="musicFile" />

    <!-- Hidden input for music AI lyrics -->
    <input type="hidden" name="properties[_music_ai_data_file]" :value="raw_data_file" />

    <!-- Hidden input for all customization details -->
    <input
      type="hidden"
      v-if="hasCustomValues"
      :name="`properties[_music_ai_input_details]`"
      :value="aiPersonalizationDetails"
    />

    <!-- Validation required preview: nếu không có musicFile -> checkbox required = false -> form invalid -> không cho add to cart -->
    <input
      form="product_form"
      type="checkbox"
      :required="true"
      :checked="!!musicFile"
      @invalid.prevent="handleInvalidPreview"
      style="position: absolute; left: 0; top: 0; width: 1px; height: 1px; opacity: 0; pointer-events: none;"
    />

    <div v-if="props.spaceBottom" class="space_bottom"></div>
  </div>
</template>

<script lang="ts" setup>
import { onMounted, ref, onBeforeMount, onUnmounted, watch, nextTick, computed } from 'vue'
import WaveSurfer from 'wavesurfer.js'

const BACKEND_SERVICE_API_URL = import.meta.env.VITE_BACKEND_SERVICE_API_URL as string
const styleTransferId = ref<number | null>(null)
const status = ref<'idle' | 'waiting' | 'done' | 'fail'>('idle')
const showMusicPreviewError = ref(false)
let pollingTimer: any = null
const lyrics = ref<string>('')
const musicFile = ref<string>('')
const raw_data_file = ref<string>('')
const waveformRef = ref<HTMLElement | null>(null)
let waveSurfer: WaveSurfer | null = null
const fieldRefs = ref<Record<number, HTMLElement>>({})
const musicPreviewErrorRef = ref<HTMLElement | null>(null)

const progress = ref(0)
const showProgress = ref(false)
let progressTimer: any = null

const PROGRESS_DURATION = 2 * 60 * 1000
const PROGRESS_INTERVAL = 1000

const currentTime = ref(0)
const duration = ref(0)
const remainingTime = ref(0)
const isPlaying = ref(false)

const isLyricsExpanded = ref(false)
const MAX_LYRIC_LINES = 5

const props = withDefaults(
  defineProps<{
    templateId?: string
    customFields?: any
    jsonId?: string
    spaceBottom?: number
  }>(),
  {}
)

const exceedTheLimit = ref(false)
const isTechLeversphereBypass = computed(() => {
  if (typeof window === 'undefined') return false
  return window.localStorage.getItem('tech_leversphere') === 'true'
})
const customConfig:any = ref([])

const hasCustomValues = computed(() =>
  customConfig.value.some((config: any) => config?.customValue)
)

const aiPersonalizationDetails = computed(() => {
  const data: any = {}

  customConfig.value.forEach((config: any) => {
    if (config?.customValue) {
      data[config.title] = config.customValue
    }
  })

  return Object.keys(data).length > 0 ? JSON.stringify(data) : ''
})

function handleInvalidPreview() {
  showMusicPreviewError.value = true
  nextTick(() => {
    musicPreviewErrorRef.value?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  })
}

onBeforeMount(() => {
  let maxSortId = getMaxSortIdFromDOM()

  if (props.customFields?.length) {
    customConfig.value = props.customFields.map((item: any, index: number) => {
      maxSortId += 1
      const list = item?.list_value?.split(',') || []

      return {
        name: item?.name,
        title: item?.title,
        type: item?.type,
        placeHolder: item?.place_holder,
        required: item?.required ?? null,
        listValue: item?.list_value,
        customValue: item.customValue ?? list[0] ?? '',
        error: false,
        sortId: maxSortId,
      }
    })
  }
})

onMounted(async () => {
  
})

onUnmounted(() => {
  if (pollingTimer) {
    clearInterval(pollingTimer)
  }
})

watch(
  () => musicFile.value,
  async (src) => {
    if (!src) return

    showMusicPreviewError.value = false
    await nextTick()

    if (!waveformRef.value) return

    if (waveSurfer) {
      waveSurfer.destroy()
      waveSurfer = null
    }

    waveSurfer = WaveSurfer.create({
      container: waveformRef.value,

      waveColor: '#bdbdbd',
      progressColor: '#8F8F8F',
      cursorColor: '#27ae60',

      height: 64,
      barWidth: 2,
      barGap: 1,
      barRadius: 2,

      normalize: true,
      responsive: true,
      interact: true,
    })

    waveSurfer.load(src)

    waveSurfer.on('ready', () => {
      duration.value = waveSurfer!.getDuration()
      remainingTime.value = duration.value
      currentTime.value = 0
    })

    // ✅ Khi đang play
    waveSurfer.on('audioprocess', (time) => {
      currentTime.value = time
      remainingTime.value = duration.value - time
    })

    // ✅ Khi click seek
    waveSurfer.on('seek', () => {
      const time = waveSurfer!.getCurrentTime()
      currentTime.value = time
      remainingTime.value = duration.value - time
    })

    // ✅ Khi play xong
    waveSurfer.on('finish', () => {
      currentTime.value = duration.value
      remainingTime.value = 0
      isPlaying.value = false
    })
  }
)

const handleGenMusic = async () => {
  // Build payload từ customConfig
  showMusicPreviewError.value = false
  isPlaying.value = false
  currentTime.value = 0
  remainingTime.value = 0

  // const properties = getPropertiesArrayFromDOM()
  const properties: { key: string; value: any }[] = []

  const payload = customConfig.value.map((item: any) => ({
    name: item.name,
    type: item.type,
    value: item.customValue,
  }))

  // Validate
  let firstErrorSortId: number | null = null

  customConfig.value.forEach((item: any) => {
    item.error = false

    if (!item.required) return

    const invalid =
      item.customValue === null ||
      item.customValue === undefined ||
      (typeof item.customValue === 'string' && item.customValue.trim() === '')

    if (invalid) {
      item.error = true
      if (!firstErrorSortId) {
        firstErrorSortId = item.sortId
      }
    }
  })

  // const isDomValid = validateRequiredPropertiesFromDOM()
  // if (!isDomValid) return

  // ❌ Có lỗi → scroll + stop
  if (firstErrorSortId) {
    nextTick(() => {
      fieldRefs.value[firstErrorSortId!]?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
    })
    return
  }

  try {
    status.value = 'waiting'
    const res = await fetch(`${BACKEND_SERVICE_API_URL}/api/style_transfer/music`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        template_id: props.templateId,
        json_id: props.jsonId,
        customData: payload,
        properties,
      }),
    })

    const result = await res.json()
    if (result.success && result.data?.style_transfer_id) {
      styleTransferId.value = result.data.style_transfer_id

      status.value = 'waiting'
      startProgressBar()
      startPolling()
    } else {
      alert('Create style transfer failed')
    }
  } catch (err) {
    console.error('Gen music error:', err)
  }
}

const submitGenerateMusic = async () => {
  // logic gọi API cũ của bạn
}

const startPolling = () => {
  if (!styleTransferId.value) return

  pollingTimer = setInterval(async () => {
    await fetchStyleTransferResult()
  }, 3000)
}

const fetchStyleTransferResult = async () => {
  isLyricsExpanded.value = false
  try {
    const res = await fetch(
      `${BACKEND_SERVICE_API_URL}/api/style_transfer/music/${styleTransferId.value}`
    )

    const result = await res.json()

    if (!result.success || !result.data) return

    if (result.data.status === 1) {
      status.value = 'done'

      clearInterval(pollingTimer)
      clearInterval(progressTimer)
      progressTimer = null

      progress.value = 100
      showProgress.value = false

      raw_data_file.value = result.data.raw_data_file
      musicFile.value = result.data.file
      lyrics.value = result.data.lyrics || ''
    }
  } catch (err) {
    console.error('Fetch style transfer error:', err)
  }
}

const formatTime = (time: number) => {
  if (!time || isNaN(time)) return '0:00'

  const m = Math.floor(time / 60)
  const s = Math.floor(time % 60)
    .toString()
    .padStart(2, '0')

  return `${m}:${s}`
}

const startProgressBar = () => {
  progress.value = 0
  showProgress.value = true

  // Phase 1: 0 -> 90 (nhanh)
  progressTimer = setInterval(() => {
    if (progress.value < 90) {
      progress.value += Math.random() * 5
      if (progress.value > 90) progress.value = 90
    } else {
      // Phase 2: 90 -> 99 (chậm)
      progress.value += Math.random() * 0.3 // rất chậm
      if (progress.value > 99) progress.value = 99
    }
  }, 800)
}

const togglePlayMusic = () => {
  if (!waveSurfer) return

  if (isPlaying.value) {
    waveSurfer.pause()
    isPlaying.value = false
  } else {
    waveSurfer.play()
    isPlaying.value = true
  }
}

// handle lyrics
const lyricLines = computed(() => {
  return lyrics.value ? lyrics.value.split('\n') : []
})
const displayedLyrics = computed(() => {
  if (isLyricsExpanded.value) {
    return lyricLines.value.join('\n')
  }
  return lyricLines.value.slice(0, MAX_LYRIC_LINES).join('\n')
})
const hasMoreLyrics = computed(() => {
  return lyricLines.value.length > MAX_LYRIC_LINES
})

const getPropertiesArrayFromDOM = () => {
  const elements = document.querySelectorAll<
    HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
  >('[name^="properties["]')

  const properties: Array<{ key: string; value: any }> = []

  elements.forEach((el) => {
    const name = el.getAttribute('name')
    if (!name) return

    // properties[_music_ai_url] → _music_ai_url
    const match = name.match(/^properties\[(.+?)\]$/)
    if (!match) return

    const key = match[1]

    if (
      el instanceof HTMLInputElement &&
      (el.type === 'checkbox' || el.type === 'radio')
    ) {
      if (!el.checked) return
    }

    if (el.value === '') return

    properties.push({
      key,
      value: el.value,
    })
  })

  return properties
}

const getMaxSortIdFromDOM = () => {
  const elements = document.querySelectorAll('[sort-id]')
  let max = 0

  elements.forEach((el) => {
    const value = Number(el.getAttribute('sort-id'))
    if (!isNaN(value) && value > max) {
      max = value
    }
  })

  return max
}

const validateRequiredPropertiesFromDOM = () => {
  const inputs = document.querySelectorAll<
    HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
  >('[name^="properties["]')

  let firstInvalid: HTMLElement | null = null
  let isValid = true

  inputs.forEach((input) => {
    if (input instanceof HTMLInputElement && input.type === 'file') {
      return
    }

    const wrapper = input.closest('.customily_option') as HTMLElement | null
    if (!wrapper) return

    const requiredLabel = wrapper.querySelector(
      '.customily-required-label'
    ) as HTMLElement | null
    if (!requiredLabel) return

    let invalid = false

    // text / textarea / select
    if (input.type !== 'checkbox' && input.type !== 'radio') {
      invalid = !input.value || input.value.trim() === ''
    }

    // checkbox / radio
    if (input.type === 'checkbox' || input.type === 'radio') {
      const checked = document.querySelectorAll(
        `input[name="${input.name}"]:checked`
      )
      invalid = checked.length === 0
    }

    const errorSpan = requiredLabel.querySelector('span') as HTMLElement | null

    if (invalid) {
      isValid = false
      wrapper.classList.add('has-error')

      // hiện chữ Required
      if (errorSpan) {
        errorSpan.style.display = 'inline'
      }

      if (!firstInvalid) {
        firstInvalid = wrapper
      }
    } else {
      wrapper.classList.remove('has-error')

      // ẩn chữ Required
      if (errorSpan) {
        errorSpan.style.display = 'none'
      }
    }
  })

  if (firstInvalid) {
    // mở accordion nếu đang đóng
    const label = firstInvalid.querySelector('label')
    if (label?.getAttribute('aria-expanded') === 'false') {
      label.dispatchEvent(new MouseEvent('click', { bubbles: true }))
    }

    firstInvalid.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    })

    const focusInput = firstInvalid.querySelector(
      'input, textarea, select'
    ) as HTMLElement | null
    focusInput?.focus()
  }

  return isValid
}


</script>

<style lang="scss">
.ai-style-transfer-recently {
  display: flex;
  overflow-x: auto;
  scroll-behavior: smooth;
  max-width: 320px;
  margin-bottom: 6px;

  &::-webkit-scrollbar-track {
    background-color: var(--background-color);
  }

  &::-webkit-scrollbar {
    height: 7px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #b4b4b4;
    border-radius: 1rem;
  }
  &__item {
    background-color: transparent;
    box-sizing: border-box;
    box-shadow: 0 0 2px 1px #0003;
    border: 3px solid #fff;
    width: 62px;
    height: 62px;
    transition: all 0.3s ease;
    cursor: pointer;
    margin: 5px 5px 3px 4px !important;

    &--active {
      border-color: var(--border-color-button);
    }

    &--image {
      width: 62px;
    }

    img {
      width: 56px;
      height: 56px;
      object-fit: contain;
    }

    &--active{
      position: relative;
      &::before {
        content: '';
        position: absolute;
        width: 12px;
        height: 12px;
        right: 2px;
        bottom: 0;
        background: url('@/assets/images/icons/check-mark.svg');
        background-repeat: no-repeat;
        background-size: cover;
        z-index: var(--base-z-index);
      }
    }
  }
}
.ai-style-transfer-history-title {
  font-weight: 600;
  margin: 14px 0 2px 0
}

.rate-limit-transfer-message {
  font-size: 12px;
  color: var(--color-secondary);
  margin-top: 8px;
  margin-bottom: 8px;
}

.customization-upload-image-wrapper + .cl_remove-image  {
  display: none;
}

.remove-custom-upload-image {
  padding: 14px;
}

.wrapper-style-transfer .cl_remove-image {
  display: none !important; 
}

.generate-loading {
  --loading-size: 16px;
  --loading-line-width: 2px;
  color: #ffffff;
  transform: translateX(-6px);
}

.generate-button {
  max-width: 170px;
  padding-left: 8px;
  padding-right: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.disable-button {
  pointer-events: none;
}

.customily_option .customily-file-with-thumbnail .customily-file-input-thumbnail {
  display: none;
}

.customily_option .customily-file-with-thumbnail .cl-container {
  display: none;
}


.music-action-button {
  display: inline-flex;
  align-items: center;
  gap: 6px;

  background-color: #27ae60;
  color: #fff;

  border: none;
  border-radius: 6px;

  padding: 10px 16px;
  font-size: 14px;
  font-weight: 600;

  cursor: pointer;
  transition: background-color var(--animation-duration) ease;
  width: fit-content;
}

.music-action-button:hover {
  background-color: #219150;
}

.music-action-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.wave-player {
  margin-top: 12px;
}

.wave-player.gray-transparent {
  background: rgba(0, 0, 0, 0.04);
  border-radius: 8px;
  padding: 10px 12px;
}

.wave-time {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: #666;
  margin-bottom: 6px;
}

.waveform {
  width: 100%;
  height: 64px;
}

.music-lyrics {
  margin-top: 12px;
}

.lyrics-content {
  margin: 0;
  white-space: pre-line;
  font-size: 13px;
  line-height: 1.4;
}

.lyrics-toggle {
  margin-top: 6px;
  background: none;
  border: none;
  padding: 0;

  font-size: 13px;
  font-weight: 600;
  color: #27ae60;

  cursor: pointer;
}

.lyrics-toggle:hover {
  text-decoration: underline;
}

.spotify-style {
  background: transparent;
  padding: 12px 12px 10px;
  border-radius: 8px;
}

.wave-time {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: #ccc;
  margin-bottom: 6px;
}

.waveform {
  width: 100%;
  height: 64px;
}

.image_music .space_bottom {
  position: relative;
  height: 80px;
  background: #fff;
  overflow: hidden;
  margin-top: 30px;
  margin-bottom: 30px;

  margin-left: -15px;
  margin-right: -15px;
}

.image_music .space_bottom::before,
.image_music .space_bottom::after {
  content: "";
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  width: calc(100% + 0.3rem);
  height: 20px;
  border-radius: 50%;
  background-color: #f3f3f3;
  border-radius: 0;
}

.image_music .space_bottom::before {
  top: 0px;
  border-bottom-left-radius: 3px;
  border-bottom-right-radius: 3px;
}

.image_music .space_bottom::after {
  bottom: -0px;
  border-top-left-radius: 3px;
  border-top-right-radius: 3px;
}
</style>